import { useEffect, useRef, useState } from 'react';
import { Search, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useOsmForwardGeocode } from '@/api/hooks/useOsmGeocode';
import { OsmHttpError } from '@/api/osmClient';
import type { GeocodeResult } from '@/types/osm';

interface SearchBarProps {
  onPick: (lng: number, lat: number, label: string) => void;
}

export default function SearchBar({ onPick }: SearchBarProps) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mut = useOsmForwardGeocode();

  useEffect(() => {
    function onClick(ev: MouseEvent) {
      if (!inputRef.current) return;
      if (!(ev.target instanceof Node)) return;
      if (!inputRef.current.parentElement?.contains(ev.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    try {
      const results = await mut.mutateAsync(q.trim());
      setOpen(true);
      if (results.length > 0) {
        const top = results[0];
        const lat = typeof top.lat === 'string' ? parseFloat(top.lat) : top.lat;
        const lon = typeof top.lon === 'string' ? parseFloat(top.lon) : top.lon;
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          onPick(lon, lat, top.display_name);
        }
      }
    } catch (err) {
      if (err instanceof OsmHttpError && err.status === 503) {
        setUnavailable(true);
      }
    }
  }

  function pickResult(r: GeocodeResult) {
    const lat = typeof r.lat === 'string' ? parseFloat(r.lat) : r.lat;
    const lon = typeof r.lon === 'string' ? parseFloat(r.lon) : r.lon;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    onPick(lon, lat, r.display_name);
    setOpen(false);
  }

  if (unavailable) {
    return (
      <div className="pointer-events-auto absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 shadow">
        Search unavailable in this environment
      </div>
    );
  }

  const results = mut.data ?? [];

  return (
    <form
      onSubmit={submit}
      className="pointer-events-auto absolute left-1/2 top-3 z-20 w-full max-w-[480px] -translate-x-1/2 px-3"
    >
      <div className="relative flex items-center gap-1 rounded-md border bg-background/95 shadow-md backdrop-blur">
        <Search className="ml-2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search address or landmark…"
          className="h-9 border-0 bg-transparent shadow-none focus-visible:ring-0"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ('');
              mut.reset();
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <Button type="submit" size="sm" className="m-1 h-7" disabled={mut.isPending}>
          {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Search'}
        </Button>
      </div>
      {open && results.length > 0 && (
        <ul className="mt-1 max-h-72 overflow-y-auto rounded-md border bg-background shadow-md">
          {results.map((r, idx) => (
            <li key={`${r.place_id ?? idx}`}>
              <button
                type="button"
                onClick={() => pickResult(r)}
                className="block w-full px-3 py-2 text-left text-xs hover:bg-accent"
              >
                {r.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {mut.isError && !unavailable && (
        <div className="mt-1 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-800">
          Geocode failed: {(mut.error as Error).message}
        </div>
      )}
    </form>
  );
}
